import React from 'react';
import PropTypes from 'prop-types';

// What this trip's repairs are estimated to cost.
//
// Operations only. It deliberately includes findings still waiting on review,
// which is exactly what a customer must not see and exactly what an operator
// needs in order to decide.
//
// Two rules from the policy show through here, and both are about not stating
// more than is known.
//
// A total appears only when every damaged finding was priced. A trip with two
// priced findings and one unpriced has a subtotal, not a total, and showing the
// subtotal in the same place a total would go understates the bill in a way
// nobody downstream can detect.
//
// "Chargeable" is shown as its own line rather than left implied. An estimate
// carrying an unreviewed finding may not reach a customer, and that rule is the
// entire reason the review queue exists — so it is stated, not inferred from a
// status word an operator has to remember the meaning of.
//
// Styled inline for the same reason as the panels beside it: bootstrap 3.4.1 is
// pinned while this screen uses Bootstrap 5 classes.

const styles = {
  panel: { border: '1px solid #e0e0e0', borderRadius: '6px', padding: '16px', marginTop: '20px', backgroundColor: '#fbfbfb' },
  heading: { fontWeight: 'bold', fontSize: '16px', marginBottom: '10px' },
  total: { fontSize: '24px', fontWeight: 'bold' },
  absent: { fontSize: '16px', color: '#b8860b', fontWeight: 'bold' },
  label: { color: '#666', fontSize: '13px', marginBottom: '12px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
  th: { textAlign: 'left', borderBottom: '2px solid #e0e0e0', padding: '6px', color: '#666', fontWeight: 'normal' },
  td: { borderBottom: '1px solid #eee', padding: '6px' },
  warn: { backgroundColor: '#fff8e5', border: '1px solid #f0d9a0', borderRadius: '4px', padding: '10px', fontSize: '13px', marginBottom: '12px' },
  unpriced: { color: '#b8860b', fontWeight: 'bold' },
  chargeable: { fontSize: '13px', marginTop: '10px' },
  scroll: { overflowX: 'auto' },
};

const money = (amount, currency) =>
  amount === null || amount === undefined ? null : `${currency || ''} ${Number(amount).toFixed(2)}`.trim();

class RepairEstimate extends React.Component {
  static propTypes = {
    estimate: PropTypes.object,
  };

  render() {
    const { estimate } = this.props;
    if (!estimate || estimate.loading) return null;

    const result = estimate.getRepairEstimate;
    // Silent when the caller is not an administrator, or the query failed. This
    // sits beneath a report that is useful on its own.
    if (!result || result.status !== 200) return null;

    const {
      costStatus, total, currency, lines, unpricedCount, awaitingReview, mixedCurrency, chargeable,
    } = result;

    if (costStatus === 'no_damage') {
      return (
        <div style={styles.panel}>
          <div style={styles.heading}>Repair estimate</div>
          <div style={styles.label}>No damage was found, so there is nothing to charge for.</div>
        </div>
      );
    }

    return (
      <div style={styles.panel}>
        <div style={styles.heading}>Repair estimate</div>

        {total !== null ? (
          <div>
            <div style={styles.total}>{money(total, currency)}</div>
            <div style={styles.label}>
              {(lines || []).length} {(lines || []).length === 1 ? 'finding' : 'findings'} priced
            </div>
          </div>
        ) : (
          <div>
            <div style={styles.absent}>No total</div>
            <div style={styles.label}>
              A total is only shown when every damaged finding has a price. Showing what happened to
              price would understate the bill.
            </div>
          </div>
        )}

        {unpricedCount > 0 && (
          <div style={styles.warn}>
            {unpricedCount} {unpricedCount === 1 ? 'finding has' : 'findings have'} no rate. Add one
            in <strong>Repair rates</strong> above, then reopen this report.
          </div>
        )}

        {mixedCurrency && (
          <div style={styles.warn}>
            The matching rates are in more than one currency, so they cannot be added up. No
            conversion is guessed at — fix the rate card.
          </div>
        )}

        {awaitingReview > 0 && (
          <div style={styles.warn}>
            {awaitingReview} {awaitingReview === 1 ? 'finding is' : 'findings are'} still awaiting
            review, so this estimate is provisional and cannot be charged.
          </div>
        )}

        <div style={styles.scroll}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Part</th>
                <th style={styles.th}>Severity</th>
                <th style={styles.th}>Confidence</th>
                <th style={styles.th}>Review</th>
                <th style={styles.th}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {(lines || []).map((line) => (
                <tr key={line.findingIndex}>
                  <td style={styles.td}>{line.partName || '—'}</td>
                  <td style={styles.td}>{line.severity}</td>
                  <td style={styles.td}>
                    {/* Absent on reports generated before the service stopped
                        discarding it. Unknown, not zero. */}
                    {typeof line.confidence === 'number' ? `${Math.round(line.confidence * 100)}%` : '—'}
                  </td>
                  <td style={styles.td}>{line.reviewState}</td>
                  <td style={styles.td}>
                    {line.amount === null
                      ? <span style={styles.unpriced}>no rate</span>
                      : `${money(line.amount, line.currency)}${line.matchedOn === 'part' ? ' (part rate)' : ''}`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={styles.chargeable}>
          {chargeable
            ? 'Every finding here has been decided and priced. This may be charged.'
            : 'This may not be charged yet.'}
        </div>
      </div>
    );
  }
}

export default RepairEstimate;
